<template>
  <ResponseTile
    v-if="showErrorTile"
    :back-button-label="$t('emailchangecodeinput.response.buttonLabel')"
    :text="$t('emailchangecodeinput.response.serviceErrorText')"
    :title="$t('emailchangecodeinput.response.serviceErrorTitle')"
    @button:back="onCancel"
  />
  <Card v-else :title="$t('emailchangecodeinput.general.title')" :footerDivider="false">
    <template v-slot:content>
      <p>
        <span
          v-dompurify-html="$t('emailchangecodeinput.general.descriptionCodeInput')"
        ></span>
      </p>
      <p v-if="showGeneralError" class="error">
        {{ $t('emailchangecodeinput.general.error') }}
      </p>
      <div id="emailcode">
        <BasicTextfield
          @input="onEmailcodeInputIsUpdated"
          id="email-change.bestaetigungs-code-input"
          pattern="^\d{3}-\d{3}-\d{3}$"
          formnovalidate="true"
          @error="toggleEmailcodeError"
          @keyup.enter="onSubmit"
          @keyup.delete="triggerEmailcodeDeletePressed"
          :value="emailcode"
          :label="$t('emailchangecodeinput.emailcode.label')"
          :status="emailcodeTextfieldStatus"
          :placeholder="$t('emailchangecodeinput.emailcode.placeholder')"
          inputmode="numeric"
          :required="true"
        >
          <template v-slot:error>
            {{ emailcodeErrorMessage }}
          </template>
        </BasicTextfield>
      </div>
    </template>
    <template v-slot:footer>
      <div class="footer-cta footer-cta--left">
        <CustomButton
          @click="onSubmit"
          buttonId="email-change.bestaetigungs-code-weiter-button"
          :pendingState="loading"
          :buttonColor="ButtonColor.PRIMARY"
        >
          {{ $t('emailchangecodeinput.confirm.text') }}
        </CustomButton>
        <CustomButton
          @click="onCancel"
          buttonId="email-change.bestaetigungs-code-abbrechen-button"
          :buttonColor="ButtonColor.LINK"
        >
          {{ $t('emailchangecodeinput.cancel.text') }}
        </CustomButton>
      </div>
    </template>
  </Card>
</template>

<script setup lang="ts">
import {
  BasicTextfield,
  ButtonColor,
  Card,
  CustomButton,
  InputStatus,
  ResponseTile
} from 'de-zurich-ui-library'
import { navigate } from '@/utils/NavigationUtils'
import { confirmEmailCodeChange } from '@/stores/accountManagementService'
import { usePoliciesStore } from '@/stores/policiesStore'
import { navigateBackFromEmailChange } from '@/components/account-management/emailchange/EmailChangeNavigation'
import { ResponseError } from 'account-api'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { is5xxError } from '@/components/account-management/accountcreation/accountcreationUtils'

const { t } = useI18n()
const policiesStore = usePoliciesStore()

const props = defineProps({
  accountId: {
    type: String,
    required: true
  }
})

const emit = defineEmits<{
  confirmEmailChange: [none: void]
}>()

const emailcode = ref('')
const emailcodeTextfieldStatus = ref<InputStatus>(InputStatus.DEFAULT)
const emailcodeErrorMessage = ref('')
const loading = ref(false)
const showGeneralError = ref(false)
const showErrorTile = ref(false)
const deletePressed = ref(false)

const toggleEmailcodeError = (): void => {
  emailcodeTextfieldStatus.value = InputStatus.ERROR
}

const onEmailcodeInputIsUpdated = (val: string): void => {
  emailcode.value = val
  emailcodeTextfieldStatus.value = InputStatus.VALID
  if (!deletePressed.value && /^.{3}(-.{3})?$/.test(emailcode.value)) {
    emailcode.value = emailcode.value + '-'
  }
  deletePressed.value = false

  if (emailcode.value.length > 11) {
    emailcode.value = emailcode.value.substring(0, 11)
  }
}

const validateEmailcode = (): void => {
  if (!emailcode.value || emailcode.value === '') {
    emailcodeTextfieldStatus.value = InputStatus.ERROR
    emailcodeErrorMessage.value = t('emailchangecodeinput.emailcode.errors.empty')
  } else if (!/^[\d-]+$/.test(emailcode.value)) {
    emailcodeTextfieldStatus.value = InputStatus.ERROR
    emailcodeErrorMessage.value = t(
      'emailchangecodeinput.emailcode.errors.invalidCharacters'
    )
  } else if (!/^\d{3}-\d{3}-\d{3}$/.test(emailcode.value)) {
    emailcodeTextfieldStatus.value = InputStatus.ERROR
    emailcodeErrorMessage.value = t('emailchangecodeinput.emailcode.errors.invalidFormat')
  }
}

const onSubmit = async (): Promise<void> => {
  showGeneralError.value = false
  showErrorTile.value = false
  validateEmailcode()
  if (emailcodeTextfieldStatus.value === InputStatus.ERROR) {
    return
  }
  loading.value = true
  try {
    await confirmEmailCodeChange(props.accountId, emailcode.value)
    emit('confirmEmailChange')
  } catch (error) {
    if (error instanceof ResponseError) {
      if (error.response.status === 401) {
        navigate('/')
      } else {
        if (is5xxError(error.response.status)) {
          showErrorTile.value = true
          return
        }
        showGeneralError.value = true
      }
    } else {
      showGeneralError.value = true
    }
  } finally {
    loading.value = false
  }
}

const onCancel = (): void => {
  navigateBackFromEmailChange(policiesStore)
}

const triggerEmailcodeDeletePressed = (): void => {
  if (
    emailcode.value.length > 3 &&
    (emailcode.value.substring(3, 4) === '-' || emailcode.value.substring(6, 7) === '-')
  ) {
    deletePressed.value = true
  }
}
</script>
